import React, { useMemo, useState } from 'react';
import { Alert, DataTable, type DataTableColumn } from '@/components/ui';
import { useHeLinePricing } from '../hooks/useHeLinePricing';
import type { HiredEquipmentGridRow } from '../types';
import InfoTooltip from '../../../components/InfoTooltip';
import type { Task } from '../../../types/Task';
import { useHiredEquipmentFieldVisibility } from '../hooks/useHiredEquipmentFieldVisibility';
import { formatCurrency } from '../utils/gridMath';
import { formatHeOvertimeFooterText } from '../utils/overtime';
import {
  formatHeSummaryQty,
  hasHePaperTicket,
  parseHeSummaryRows,
  sumHeSummaryValues,
  type HeSummaryField,
  type HeSummaryRow,
  heAnchorFromForm,
  parseHeGridRows,
} from '../utils/heSummaryRows';

interface HiredEquipmentSummaryTableProps {
  fields: HeSummaryField[];
  availableTasks: Task[];
  /** V2-047: the submission, so its compliance warnings are checked where approvers decide. */
  formId?: string;
}

const HiredEquipmentSummaryTable: React.FC<HiredEquipmentSummaryTableProps> = ({
  fields,
  availableTasks,
  formId,
}) => {
  const { showFuel } = useHiredEquipmentFieldVisibility();
  // V2-047 / H4: the same compliance warnings as Detailed View (expired vehicle insurance, missing
  // WCB …), from the same server check, read-only – nothing is re-priced here.
  const [complianceRows, setComplianceRows] = useState<HiredEquipmentGridRow[]>(() =>
    parseHeGridRows(fields, availableTasks),
  );
  useHeLinePricing(complianceRows, setComplianceRows, {
    serviceAreaId: heAnchorFromForm(fields).serviceAreaId,
    excludeFormId: formId ?? null,
    enabled: false,
    checkCompliance: formId != null,
  });
  const complianceWarnings = useMemo(() => {
    const byText = new Map<string, number[]>();
    complianceRows.forEach((row, index) =>
      (row.complianceWarnings ?? []).forEach((w) => byText.set(w, [...(byText.get(w) ?? []), index + 1])),
    );
    return Array.from(byText.entries());
  }, [complianceRows]);
  const rows = useMemo(
    () => parseHeSummaryRows(fields, availableTasks),
    [fields, availableTasks],
  );
  const totalValue = sumHeSummaryValues(rows);
  const footerOtText = formatHeOvertimeFooterText(
    rows.map((row) => ({ ...row, fuelPerHour: row.fuelPriceAdjustment ?? 0 })),
  );

  const columns: DataTableColumn<HeSummaryRow>[] = [
    {
      key: 'date',
      header: 'Date',
      mobile: 'subtitle',
      className: 'tw-whitespace-nowrap',
      headerClassName: 'tw-min-w-[7rem]',
      cell: (row) => row.dateLabel,
    },
    {
      key: 'po',
      header: 'PO',
      className: 'tw-whitespace-nowrap',
      headerClassName: 'tw-min-w-[6rem]',
      cell: (row) => row.po,
    },
    {
      key: 'task',
      header: 'Task',
      mobile: 'title',
      // `tw-block tw-truncate`: without this, a long task description wraps and lets the
      // column shrink to its narrowest breakable word, stealing width from the short
      // columns next to it (Fuel included). Truncating keeps every column at a stable,
      // legible width; `title` still surfaces the full text on hover.
      headerClassName: 'tw-min-w-[12rem]',
      cell: (row) => (
        <span className="tw-block tw-truncate" title={row.taskLabel}>
          {row.taskLabel}
        </span>
      ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      headerClassName: 'tw-min-w-[9rem]',
      cell: (row) => (
        <span className="tw-block tw-truncate" title={row.vendor}>
          {row.vendor}
        </span>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      headerClassName: 'tw-min-w-[9rem]',
      cell: (row) => (
        <span className="tw-block tw-truncate" title={row.resource}>
          {row.resource}
        </span>
      ),
    },
    {
      key: 'qty',
      header: 'Qty',
      align: 'right',
      mobileLabel: 'Qty',
      headerClassName: 'tw-min-w-[5rem]',
      cell: (row) => (
        <span className="tw-tabular-nums">{row.lumpSum ? '–' : formatHeSummaryQty(row.qty)}</span>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      align: 'right',
      headerClassName: 'tw-min-w-[6rem]',
      cell: (row) => (
        <span className="tw-whitespace-nowrap tw-tabular-nums">
          {row.lumpSum || row.rate == null ? '–' : formatCurrency(row.rate)}
        </span>
      ),
    },
    {
      key: 'fuel',
      header: 'Fuel',
      align: 'right',
      hidden: !showFuel,
      headerClassName: 'tw-min-w-[6rem]',
      cell: (row) => (
        <span className="tw-whitespace-nowrap tw-tabular-nums">
          {row.fuelPriceAdjustment == null || row.fuelPriceAdjustment <= 0
            ? '–'
            : formatCurrency(row.fuelPriceAdjustment)}
        </span>
      ),
    },
    {
      key: 'ot',
      header: 'OT Eligible',
      headerClassName: 'tw-min-w-[7rem]',
      cell: (row) => (row.overtimeEligible ? 'Yes' : '–'),
    },
    {
      key: 'value',
      header: 'Amount',
      align: 'right',
      mobile: 'trailing',
      headerClassName: 'tw-min-w-[7rem]',
      cell: (row) => {
        const value = (
          <span className="tw-whitespace-nowrap tw-font-bold tw-tabular-nums tw-text-content">
            {formatCurrency(row.value)}
          </span>
        );
        if (!row.otBreakdown) return value;
        return (
          <InfoTooltip content={<span className="tw-whitespace-pre-line">{row.otBreakdown}</span>} id={`he-sum-ot-${row.key}`}>
            {value}
          </InfoTooltip>
        );
      },
    },
    {
      key: 'ticket',
      header: 'Ticket',
      className: 'tw-whitespace-nowrap',
      headerClassName: 'tw-min-w-[6rem]',
      hidden: !rows.some((row) => hasHePaperTicket(row.ticket)),
      cell: (row) => row.ticket,
    },
  ];

  const visibleCount = columns.filter((c) => !c.hidden).length;
  const ticketVisible = rows.some((row) => hasHePaperTicket(row.ticket));
  const valueFooterLead = Math.max(1, visibleCount - 1 - (ticketVisible ? 1 : 0));

  return (
    <div>
      {complianceWarnings.length > 0 && (
        // STATE-05: one banner listing everything outstanding, saying plainly it does not block.
        <Alert variant="warning" className="tw-mb-2" data-testid="he-summary-compliance">
          <strong>Compliance warnings</strong> – these do not block approval.
          <ul className="tw-mb-0 tw-mt-1 tw-pl-4">
            {complianceWarnings.map(([warning, lines]) => (
              <li key={warning}>
                {warning} ({lines.length === 1 ? 'line' : 'lines'} {lines.join(', ')})
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <DataTable
        columns={columns}
        data={rows}
        rowKey={(row) => row.key}
        size="sm"
        hover
        emptyMessage="No line items recorded for this submission"
        footer={
          rows.length > 0 ? (
            <tr>
              <td colSpan={valueFooterLead} className="tw-text-right tw-text-content">
                Total
              </td>
              <td className="tw-text-right tw-font-bold tw-tabular-nums tw-text-content">
                {footerOtText ? (
                  <InfoTooltip content={<span className="tw-whitespace-pre-line">{footerOtText}</span>} id="he-sum-footer-ot">
                    {formatCurrency(totalValue)}
                  </InfoTooltip>
                ) : (
                  formatCurrency(totalValue)
                )}
              </td>
              {ticketVisible ? <td aria-hidden="true" /> : null}
            </tr>
          ) : undefined
        }
      />
      {rows.length > 0 && (
        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface-alt tw-px-3 tw-py-2.5 tw-font-bold tw-text-content md:tw-hidden">
          <span>Total</span>
          <span className="tw-tabular-nums">{formatCurrency(totalValue)}</span>
        </div>
      )}
    </div>
  );
};

export default HiredEquipmentSummaryTable;
